import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { familyEntryByRole } from '@eneco/cms-sanity';
import RichText from '@eneco/components/RichText/RichText';
import { NotificationBox, Stack, Text } from '@eneco/sparky';

import type { FormFieldDescriptor } from './types';

/**
 * Body shared by the choice fields — radio group, select, salutation.
 *
 * **Renders no options, and contributes no validation, on purpose.** Each descriptor's `options` is
 * an array of `migrationUnresolvedReference` stubs (ADR-0049) carrying only `label`/`note`: there
 * are no choices in the content to show. Two consequences follow, and both are deliberate:
 *
 * - No control is registered with react-hook-form, because a control with nothing to pick is not a
 *   control. Inventing options would fabricate content.
 * - No rule is contributed either. A `requiredMessage` on a field nobody can fill would make the
 *   whole form permanently unsubmittable — strictly worse than letting the rest of it work.
 *
 * The label still renders so the form's shape is right, with a dev-only notice so an empty choice
 * reads as "not migrated" rather than "broken". It starts working unchanged once those references
 * point at real option documents.
 */
export const ChoiceField: FC<{ family: string; role: string }> = ({ family, role }) => {
  const { adapter } = useCms();
  const content = useContent<Record<string, unknown>>();

  const field = (familyEntryByRole(content[family], role) ?? {}) as FormFieldDescriptor;
  const hint = adapter.toRichText(field.hint);

  return (
    <Stack gap="2">
      {field.label && <Text weight="bold">{field.label}</Text>}
      {hint && (
        <Text size="BodyS">
          <RichText html={hint} />
        </Text>
      )}
      {process.env.NODE_ENV !== 'production' && (
        <NotificationBox isAlert={false} title="Options not migrated" text="This field's choices are still unresolved references." />
      )}
    </Stack>
  );
};
